import { useQueryClient } from '@tanstack/react-query';
import {
    useLocation,
    useParams as useRouteParams,
    useRouter as useTanStackRouter
} from '@tanstack/react-router';
import {
    forwardRef,
    useCallback,
    useEffect,
    useMemo,
    type AnchorHTMLAttributes,
    type MouseEvent
} from 'react';

// Navigation helpers with the shape of the Next.js APIs the components were
// written against: useRouter, useSearchParams, usePathname, useParams, and
// Link. TanStack Router does the routing underneath.

export interface AppRouter {
    push(href: string, options?: { scroll?: boolean }): void;
    replace(href: string, options?: { scroll?: boolean }): void;
    /** Refetches every query on the page, like a Next.js router refresh. */
    refresh(): void;
    back(): void;
    forward(): void;
    prefetch(href: string): void;
}

export function useRouter(): AppRouter {
    const router = useTanStackRouter();
    const queryClient = useQueryClient();
    return useMemo(
        () => ({
            push: (href, options) =>
                void router.navigate({
                    href,
                    resetScroll: options?.scroll !== false
                }),
            replace: (href, options) =>
                void router.navigate({
                    href,
                    replace: true,
                    resetScroll: options?.scroll !== false
                }),
            refresh: () => void queryClient.invalidateQueries(),
            back: () => router.history.back(),
            forward: () => router.history.forward(),
            prefetch: () => undefined
        }),
        [router, queryClient]
    );
}

export function usePathname(): string {
    return useLocation({ select: (location) => location.pathname });
}

/** The current query string. Treat it as read-only. */
export function useSearchParams(): URLSearchParams {
    const search = useLocation({ select: (location) => location.searchStr });
    return useMemo(() => new URLSearchParams(search), [search]);
}

export function useParams<
    T extends Record<string, string> = Record<string, string>
>(): T {
    return useRouteParams({ strict: false } as never) as T;
}

type LinkProps = Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'href'> & {
    href: string;
    /** Accepted for compatibility; links do not prefetch. */
    prefetch?: boolean;
    replace?: boolean;
    scroll?: boolean;
};

function isPlainLeftClick(event: MouseEvent<HTMLAnchorElement>): boolean {
    return (
        event.button === 0 &&
        !event.metaKey &&
        !event.ctrlKey &&
        !event.shiftKey &&
        !event.altKey
    );
}

/** An anchor that navigates inside the app without a page load. */
export const Link = forwardRef<HTMLAnchorElement, LinkProps>(function Link(
    { href, prefetch: _prefetch, replace, scroll, onClick, target, ...props },
    ref
) {
    const router = useTanStackRouter();
    const handleClick = useCallback(
        (event: MouseEvent<HTMLAnchorElement>) => {
            onClick?.(event);
            if (event.defaultPrevented || !isPlainLeftClick(event)) return;
            if (target && target !== '_self') return;
            const url = new URL(href, window.location.href);
            if (url.origin !== window.location.origin) return;
            if (url.pathname.startsWith('/api/')) return;
            event.preventDefault();
            void router.navigate({
                href: url.pathname + url.search + url.hash,
                replace,
                resetScroll: scroll !== false
            });
        },
        [href, onClick, replace, router, scroll, target]
    );
    return (
        <a
            ref={ref}
            href={href}
            target={target}
            onClick={handleClick}
            {...props}
        />
    );
});

/** Thrown by `redirect()` and `notFound()`; the route error boundary handles it. */
export class NavigationSignal extends Error {
    constructor(
        readonly kind: 'redirect' | 'not-found',
        readonly href: string | null
    ) {
        super(kind === 'redirect' ? `Redirect to ${href}` : 'Not found');
        this.name = 'NavigationSignal';
    }
}

/** Next.js `redirect()`: stops rendering and navigates to `href`. */
export function redirect(href: string): never {
    throw new NavigationSignal('redirect', href);
}

export function permanentRedirect(href: string): never {
    return redirect(href);
}

/** Next.js `notFound()`: renders the route's "Not found" state. */
export function notFound(): never {
    throw new NavigationSignal('not-found', null);
}

/** Navigates to `href` once rendered, like Next.js redirect() in a page. */
export function NavigateTo({
    href,
    replace = true
}: {
    href: string;
    replace?: boolean;
}) {
    const router = useTanStackRouter();
    useEffect(() => {
        void router.navigate({ href, replace });
    }, [href, replace, router]);
    return null;
}
